import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Clock, HelpCircle, BarChart3, RefreshCw, Search,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { EmptyState } from "../ui";
import "./StudentAssessments.scss";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned Exam" };
const STATUS_LABELS = {
  available: "Available",
  in_progress: "In Progress",
  completed: "Completed",
  expired: "Expired",
  upcoming: "Upcoming",
};

const StudentAssessmentsPage = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/student-assessments");
      setAssessments(data.assessments || []);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);

  const filtered = assessments.filter((a) => {
    if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && a.studentStatus !== statusFilter) return false;
    return true;
  });

  const handleAction = (a) => {
    if (a.studentStatus === "in_progress" && a.inProgressAttemptId) {
      navigate(`/s/assessment/take/${a.inProgressAttemptId}`);
    } else {
      navigate(`/s/assessment/${a._id}`);
    }
  };

  const getActionLabel = (a) => {
    switch (a.studentStatus) {
      case "in_progress": return "Continue";
      case "completed": return "View Results";
      case "available": return "Start";
      default: return "View";
    }
  };

  const getActionVariant = (a) => {
    switch (a.studentStatus) {
      case "in_progress": return "warning";
      case "available": return "primary";
      default: return "outline-secondary";
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div className="student-assessments-page">
      <h4 className="mb-3 fw-bold">My Assessments</h4>

      <div className="assessments-toolbar">
        <div className="position-relative flex-grow-1" style={{ minWidth: 200 }}>
          <Search size={14} className="position-absolute top-50 translate-middle-y ms-2" style={{ color: 'var(--text-tertiary)' }} />
          <input
            className="search-input ps-4 w-100"
            placeholder="Search assessments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="available">Available</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
          <option value="upcoming">Upcoming</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<HelpCircle size={40} />}
          title="No Assessments"
          description={search || statusFilter ? "No assessments match your filters" : "No assessments assigned to you yet"}
          action={search || statusFilter ? { label: "Clear Filters", onClick: () => { setSearch(""); setStatusFilter(""); } } : null}
        />
      ) : (
        filtered.map((a) => (
          <div key={a._id} className="sa-card">
            <div className="sa-card__header">
              <div>
                <div className="sa-card__badges">
                  <span className={`sa-card__status sa-card__status--${a.studentStatus}`}>
                    {STATUS_LABELS[a.studentStatus]}
                  </span>
                  <span className="sa-card__mode">{MODE_LABELS[a.mode]}</span>
                </div>
                <div className="sa-card__title">{a.title}</div>
                {a.description && <div className="sa-card__description">{a.description}</div>}
              </div>
            </div>

            <div className="sa-card__meta">
              <span><HelpCircle size={14} /> {a.questionCount} questions</span>
              <span><Clock size={14} /> {a.timeLimit > 0 ? `${a.timeLimit} min` : "No limit"}</span>
              <span><RefreshCw size={14} /> {a.attemptsUsed}/{a.maxAttempts > 0 ? a.maxAttempts : "∞"} attempts</span>
              {a.bestScore > 0 && <span><BarChart3 size={14} /> Best: {a.bestScore}%</span>}
            </div>

            <div className="sa-card__footer">
              <div className="sa-card__score">
                {a.startDate && <span>Starts: {new Date(a.startDate).toLocaleDateString()}</span>}
                {a.endDate && <span className="ms-3">Ends: {new Date(a.endDate).toLocaleDateString()}</span>}
              </div>
              <Button
                size="sm"
                variant={getActionVariant(a)}
                onClick={() => handleAction(a)}
                disabled={a.studentStatus === "expired" || a.studentStatus === "upcoming"}
              >
                {getActionLabel(a)}
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default StudentAssessmentsPage;
